import {convertImageToWebP, getCountryCode, sanitizeFileName} from '@/helpers/shadcn/utils'
import {useAzureBlob} from '@/hooks/useAzureBlob-hook'
import {deleteProductMediaService, getSastoken, updateProductMediaMetadata} from '@/services/productMedia'
import {useAppDispatch} from '@/store'
import {setToastNotifications} from '@/store/slices/commonSlice'
import {FILE, SUPPORTED_PRODUCT_MEDIA} from '@/utils/constants/commonConstants'
import {growthBookFeatureFlags} from '@/utils/constants/growthBookFeatureFlags'
import {move} from '@dnd-kit/helpers'
import {useFeatureValue} from '@growthbook/growthbook-react'
import {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {useParams} from 'react-router-dom'
import {useProductReview} from '../../../../context/ProductReviewContext'

/**
 * @typedef {Object} MediaSlotError
 * @property {'info'|'error'} feedbackType Feedback type shown by the file upload control.
 * @property {string} helperText Helper text shown under the file upload control.
 */

/** @type {MediaSlotError} */
const INITIAL_SLOT_ERROR = {feedbackType: 'info', helperText: ''}
const supportedProductMediaDescription = SUPPORTED_PRODUCT_MEDIA.join(', ')

/**
 * Slot configuration: slot 1 only accepts still images, slots 2-3 also accept GIFs and MP4 videos.
 */
const MEDIA_SLOT_RULES = {
  1: {
    allowedMediaKinds: ['IMAGE'],
    accept: '.jpg,.png,.webp',
    helperText: 'Only JPG, PNG or WEBP image files are accepted.',
  },
  2: {
    allowedMediaKinds: ['IMAGE', 'GIF', 'MP4'],
    accept: SUPPORTED_PRODUCT_MEDIA.join(','),
    helperText: `Accepts ${supportedProductMediaDescription} files.`,
  },
  3: {
    allowedMediaKinds: ['IMAGE', 'GIF', 'MP4'],
    accept: SUPPORTED_PRODUCT_MEDIA.join(','),
    helperText: `Accepts ${supportedProductMediaDescription} files.`,
  },
}

const MEDIA_SLOT_DISPLAY_ORDERS = Object.keys(MEDIA_SLOT_RULES).map(Number)

/** Media types that may never occupy the first media slot (`displayOrder` 1). */
const FIRST_SLOT_DISALLOWED_MEDIA_TYPES = ['VIDEO']
const FIRST_SLOT_VIDEO_ERROR_TEXT = "A video can't be placed in the first media slot. Move it before saving."

/**
 * Computes the display-order remapping produced by a drag-and-drop reorder event.
 *
 * `slots` must carry a stable `id` that follows each slot's *content* (not its position) across
 * renders - dnd-kit's sortable tracking (and React's own reconciliation, keyed off the same id)
 * relies on a dragged item's id appearing at its new index on the next render. Using the fixed
 * position (`displayOrder`) as the id instead means every render always pairs id 1 with index 0,
 * id 2 with index 1, etc., so dnd-kit never "sees" anything move; the drop instead gets partially
 * (or only visually) applied and needs a second drag to catch up.
 *
 * @param {{id: string, displayOrder: number, mediaType: string}[]} slots
 * @param {import('@dnd-kit/dom').DragEndEvent} event
 * @returns {Map<number, number>} Old-to-new display order map.
 */
const computeReorderedDisplayOrders = (slots, event) => {
  const reordered = move(
    slots.map(slot => ({id: slot.id, displayOrder: slot.displayOrder, mediaType: slot.mediaType})),
    event
  )

  return new Map(reordered.map((slot, index) => [slot.displayOrder, index + 1]))
}

/**
 * Remaps the keys of a display-order-keyed object using an old-to-new display order map.
 *
 * @template T
 * @param {Object.<number, T>} record
 * @param {Map<number, number>} displayOrderMap
 * @returns {Object.<number, T>}
 */
const remapByDisplayOrder = (record, displayOrderMap) => {
  const next = {}

  Object.entries(record).forEach(([displayOrder, value]) => {
    next[displayOrderMap.get(Number(displayOrder)) ?? Number(displayOrder)] = value
  })

  return next
}

const resolveMediaKind = file => {
  const fileType = (file?.type || '').toLowerCase()

  if (fileType === 'image/gif') return 'GIF'
  if (fileType === 'video/mp4') return 'MP4'
  if (fileType.startsWith('image/')) return 'IMAGE'

  return 'UNSUPPORTED'
}

const getMediaTitle = mediaKind => {
  if (mediaKind === 'MP4') return 'mp4'
  if (mediaKind === 'GIF') return 'gif'
  return 'image'
}

const renameFile = (file, fileName) =>
  fileName !== file.name ? new File([file], fileName, {type: file.type, lastModified: file.lastModified}) : file

/**
 * Manages the 3 product media slots (image / GIF / MP4). Selecting a file only validates it,
 * converts images to WebP and stores a local preview in the Product Review context - nothing is
 * sent to the server yet, so navigating away or cancelling never leaves orphaned files behind.
 * The actual SAS token request, Azure Blob upload and any pending delete only run right before
 * the product is saved (registered with the context as a "before save" handler), keeping network
 * side effects tied to clicking Save/Publish rather than to picking a file.
 */
export const useUploadProductMedia = () => {
  const dispatch = useAppDispatch()
  const {id: productId} = useParams()
  const {productData, isEditMode, setEditableField, registerMediaSaveHandler} = useProductReview()
  const {uploadFileOnBlob, uploadVideoOrGifOnBlob} = useAzureBlob()
  const {maxIndividualImageSize = '2MB'} = useFeatureValue(growthBookFeatureFlags.AdminBffImageValidationRules) || {}

  const [slotUiState, setSlotUiState] = useState(
    /** @type {Object.<number, {uploading?: boolean, error?: MediaSlotError}>} */ ({})
  )
  /** Files selected but not yet uploaded, keyed by display order. */
  const [pendingUploads, setPendingUploads] = useState(
    /** @type {Object.<number, {file: File, mediaKind: string, previewUrl: string}>} */ ({})
  )
  /** Display orders of previously-saved media the user removed, to be deleted on save. */
  const [pendingDeletions, setPendingDeletions] = useState(/** @type {Set<number>} */ (new Set()))
  /** Whether the slots have been reordered since the last save, regardless of any pending uploads/deletions. */
  const [hasPendingReorder, setHasPendingReorder] = useState(false)

  const pendingUploadsRef = useRef(pendingUploads)
  pendingUploadsRef.current = pendingUploads

  const hasPendingReorderRef = useRef(hasPendingReorder)
  hasPendingReorderRef.current = hasPendingReorder

  const updateSlotUiState = useCallback((displayOrder, updates) => {
    setSlotUiState(prev => ({
      ...prev,
      [displayOrder]: {
        uploading: false,
        error: INITIAL_SLOT_ERROR,
        ...prev[displayOrder],
        ...updates,
      },
    }))
  }, [])

  const mediaByDisplayOrder = useMemo(() => {
    const media = Array.isArray(productData?.media) ? productData.media : []
    return new Map(media.map(item => [Number(item?.displayOrder), item]))
  }, [productData?.media])

  const mediaSlots = useMemo(
    () =>
      MEDIA_SLOT_DISPLAY_ORDERS.map(displayOrder => {
        const existingMedia = mediaByDisplayOrder.get(displayOrder)
        const uiState = slotUiState[displayOrder] || {}
        const error = uiState.error || INITIAL_SLOT_ERROR

        return {
          // Stable id that follows this slot's content (not its position) across renders, so
          // dnd-kit's sortable tracking and React's own reconciliation can tell a slot actually
          // moved instead of always seeing the same id pinned to the same index. Falls back to a
          // per-position placeholder when the slot has no media yet.
          id: existingMedia?.mediaUrl || `empty-slot-${displayOrder}`,
          displayOrder,
          mediaUrl: existingMedia?.mediaUrl || '',
          mediaType: existingMedia?.mediaType || '',
          accept: MEDIA_SLOT_RULES[displayOrder].accept,
          uploading: uiState.uploading || false,
          feedbackType: error.feedbackType,
          helperText: error.feedbackType === 'error' ? error.helperText : MEDIA_SLOT_RULES[displayOrder].helperText,
        }
      }),
    [mediaByDisplayOrder, slotUiState]
  )

  const replaceMediaAtDisplayOrder = useCallback(
    (displayOrder, mediaItem) => {
      const currentMedia = Array.isArray(productData?.media) ? productData.media : []
      const nextMedia = currentMedia.filter(item => Number(item?.displayOrder) !== displayOrder)

      if (mediaItem) {
        nextMedia.push(mediaItem)
      }

      setEditableField(
        'media',
        nextMedia.sort((left, right) => Number(left?.displayOrder) - Number(right?.displayOrder))
      )
    },
    [productData?.media, setEditableField]
  )

  // Discard any local (not-yet-saved) media selections once edit mode ends - on cancel this
  // drops unsaved previews, and after a successful save the pending state is already empty.
  useEffect(() => {
    if (isEditMode) return

    setPendingUploads(prev => {
      Object.values(prev).forEach(pending => URL.revokeObjectURL(pending.previewUrl))
      return {}
    })
    setPendingDeletions(new Set())
    setHasPendingReorder(false)
  }, [isEditMode])

  useEffect(
    () => () => {
      Object.values(pendingUploadsRef.current).forEach(pending => URL.revokeObjectURL(pending.previewUrl))
    },
    []
  )

  const handleUpload = useCallback(
    async (displayOrder, file) => {
      if (!file) return

      const slotRule = MEDIA_SLOT_RULES[displayOrder]
      const mediaKind = resolveMediaKind(file)

      if (!slotRule?.allowedMediaKinds.includes(mediaKind)) {
        updateSlotUiState(displayOrder, {
          error: /** @type {MediaSlotError} */ ({
            feedbackType: 'error',
            helperText: slotRule?.helperText || `Please use ${supportedProductMediaDescription} files only.`,
          }),
        })
        return
      }

      const maxSizeMB = Number(maxIndividualImageSize?.match(/\d+/)?.[0]) || 2
      if (mediaKind === 'IMAGE' && file.size > maxSizeMB * FILE.SIZE_BYTES) {
        updateSlotUiState(displayOrder, {
          error: /** @type {MediaSlotError} */ ({
            feedbackType: 'error',
            helperText: `Each image must be less than or equal to ${maxSizeMB} MB.`,
          }),
        })
        return
      }

      try {
        updateSlotUiState(displayOrder, {uploading: true, error: INITIAL_SLOT_ERROR})

        const fileToStore =
          mediaKind === 'IMAGE' ? (await convertImageToWebP(file, {entityType: 'product'}))?.webpFile : file

        if (!fileToStore) {
          throw new Error('Image or metadata is missing. Please try again.')
        }

        const sanitizedFile = renameFile(fileToStore, sanitizeFileName(fileToStore.name))
        const previewUrl = URL.createObjectURL(sanitizedFile)

        setPendingUploads(prev => {
          const previous = prev[displayOrder]
          if (previous?.previewUrl) URL.revokeObjectURL(previous.previewUrl)
          return {...prev, [displayOrder]: {file: sanitizedFile, mediaKind, previewUrl}}
        })
        setPendingDeletions(prev => {
          if (!prev.has(displayOrder)) return prev
          const next = new Set(prev)
          next.delete(displayOrder)
          return next
        })

        replaceMediaAtDisplayOrder(displayOrder, {
          mediaUrl: previewUrl,
          displayOrder,
          mediaType: mediaKind === 'MP4' ? 'VIDEO' : 'IMAGE',
          mediaTitle: getMediaTitle(mediaKind),
        })
      } catch (error) {
        updateSlotUiState(displayOrder, {
          error: /** @type {MediaSlotError} */ ({
            feedbackType: 'error',
            helperText: error?.message || 'Failed to process file. Please try again.',
          }),
        })
      } finally {
        updateSlotUiState(displayOrder, {uploading: false})
      }
    },
    [maxIndividualImageSize, replaceMediaAtDisplayOrder, updateSlotUiState]
  )

  const handleUploadError = useCallback(
    (displayOrder, error) => {
      updateSlotUiState(displayOrder, {
        error: /** @type {MediaSlotError} */ ({
          feedbackType: 'error',
          helperText: error || `Invalid file format. Please use ${supportedProductMediaDescription} files only.`,
        }),
      })
    },
    [updateSlotUiState]
  )

  const handleDelete = useCallback(
    displayOrder => {
      const pending = pendingUploads[displayOrder]

      if (pending) {
        URL.revokeObjectURL(pending.previewUrl)
        setPendingUploads(prev => {
          const next = {...prev}
          delete next[displayOrder]
          return next
        })
      } else if (mediaByDisplayOrder.has(displayOrder)) {
        setPendingDeletions(prev => new Set(prev).add(displayOrder))
      }

      updateSlotUiState(displayOrder, {error: INITIAL_SLOT_ERROR})
      replaceMediaAtDisplayOrder(displayOrder, null)
    },
    [mediaByDisplayOrder, pendingUploads, replaceMediaAtDisplayOrder, updateSlotUiState]
  )

  // Handles a drag-and-drop reorder of the media slots. Always applies the move (a video can be
  // dropped into the first slot) and remaps displayOrder across the saved media, any pending
  // (not-yet-uploaded) files, pending deletions and per-slot UI state, so everything stays
  // associated with the moved slot rather than its old position. Placing a video in the first
  // slot shows a toast (it doesn't block the move - trying to reject/undo the drop instead left
  // the dragged item visually stuck in the dropped spot, since dnd-kit's `dragend` event has no
  // reliable way to force a snap-back once the drop has already been applied) and is separately
  // caught by `validateMediaFields`, which disables the Save button until it's fixed.
  const handleReorder = useCallback(
    event => {
      const displayOrderMap = computeReorderedDisplayOrders(mediaSlots, event)
      const hasMoved = [...displayOrderMap].some(([oldOrder, newOrder]) => oldOrder !== newOrder)
      if (!hasMoved) return

      const currentMedia = Array.isArray(productData?.media) ? productData.media : []
      const nextMedia = currentMedia
        .map(item => ({
          ...item,
          displayOrder: displayOrderMap.get(Number(item?.displayOrder)) ?? item?.displayOrder,
        }))
        .sort((left, right) => Number(left?.displayOrder) - Number(right?.displayOrder))

      setEditableField('media', nextMedia)
      setPendingUploads(prev => remapByDisplayOrder(prev, displayOrderMap))
      setPendingDeletions(prev => new Set([...prev].map(order => displayOrderMap.get(order) ?? order)))
      setSlotUiState(prev => remapByDisplayOrder(prev, displayOrderMap))
      setHasPendingReorder(true)

      const firstSlotMedia = nextMedia.find(item => Number(item?.displayOrder) === 1)
      if (FIRST_SLOT_DISALLOWED_MEDIA_TYPES.includes(firstSlotMedia?.mediaType)) {
        dispatch(
          setToastNotifications({
            show: true,
            message: FIRST_SLOT_VIDEO_ERROR_TEXT,
            type: 'error',
          })
        )
      }
    },
    [dispatch, mediaSlots, productData?.media, setEditableField]
  )

  // Runs right before the product is saved: deletes any removed server-side media, uploads any
  // newly-selected files to Azure Blob and swaps their local preview URL for the real CDN URL.
  const commitPendingMedia = useCallback(
    async currentProductData => {
      const uploadEntries = Object.entries(pendingUploadsRef.current).map(([displayOrder, pending]) => ({
        displayOrder: Number(displayOrder),
        ...pending,
      }))
      const deletions = [...pendingDeletions]

      if (uploadEntries.length === 0 && deletions.length === 0 && !hasPendingReorderRef.current) {
        return currentProductData
      }

      uploadEntries.forEach(entry => updateSlotUiState(entry.displayOrder, {uploading: true}))

      try {
        for (const displayOrder of deletions) {
          await deleteProductMediaService({productId, displayOrder})
        }

        let nextMedia = Array.isArray(currentProductData?.media) ? [...currentProductData.media] : []

        if (uploadEntries.length > 0) {
          const sasTokenResponse = await getSastoken({
            productId,
            media: uploadEntries.map(entry => ({
              fileName: entry.file.name,
              mediaType: entry.mediaKind === 'MP4' ? 'VIDEO' : 'IMAGE',
              displayOrder: entry.displayOrder,
              fileSize: entry.file.size,
            })),
            countryCode: getCountryCode(),
            salesOrg: currentProductData?.additionalInfo?.salesOrg,
          })
          const sasMediaItems = sasTokenResponse?.media ?? sasTokenResponse?.data?.media ?? []

          if (sasMediaItems.length !== uploadEntries.length) {
            throw new Error('Failed to get upload authorization for one of the selected files.')
          }

          for (const [index, sasMedia] of sasMediaItems.entries()) {
            const entry = uploadEntries.find(item => item.file.name === sasMedia?.fileName) ?? uploadEntries[index]
            const displayOrder = entry?.displayOrder

            if (!entry || !sasMedia?.sasToken || !sasMedia?.directory || !sasMedia?.cdnUrl) {
              throw new Error('Failed to get upload authorization for one of the selected files.')
            }

            const renamedFile =
              sasMedia.fileName && sasMedia.fileName !== entry.file.name
                ? new File([entry.file], sasMedia.fileName, {type: entry.file.type})
                : entry.file

            const uploadToBlob = entry.mediaKind === 'IMAGE' ? uploadFileOnBlob : uploadVideoOrGifOnBlob
            const blobUploadResult = await uploadToBlob(
              {sasToken: sasMedia.sasToken, directory: sasMedia.directory},
              renamedFile
            )

            if (blobUploadResult !== true) {
              throw new Error(blobUploadResult?.message || `Failed to upload the file for slot ${displayOrder}.`)
            }

            nextMedia = nextMedia.filter(item => Number(item?.displayOrder) !== displayOrder)
            nextMedia.push({
              mediaUrl: sasMedia.cdnUrl,
              displayOrder,
              mediaType: entry.mediaKind === 'MP4' ? 'VIDEO' : 'IMAGE',
              mediaTitle: getMediaTitle(entry.mediaKind),
            })
          }
        }

        // Persist the final media order/metadata whenever it may have changed - either because
        // new files were uploaded above, or because the slots were reordered via drag-and-drop
        // (with no new uploads/deletions of its own).
        if (uploadEntries.length > 0 || hasPendingReorderRef.current) {
          const sortedMedia = nextMedia.sort((left, right) => Number(left?.displayOrder) - Number(right?.displayOrder))

          await updateProductMediaMetadata({
            productId,
            media: sortedMedia,
            countryCode: getCountryCode(),
            salesOrg: currentProductData?.additionalInfo?.salesOrg,
          })

          nextMedia = sortedMedia
        }

        uploadEntries.forEach(entry => URL.revokeObjectURL(entry.previewUrl))
        setPendingUploads({})
        setPendingDeletions(new Set())
        setHasPendingReorder(false)

        return {
          ...currentProductData,
          media: nextMedia.sort((left, right) => Number(left?.displayOrder) - Number(right?.displayOrder)),
        }
      } catch (error) {
        uploadEntries.forEach(entry =>
          updateSlotUiState(entry.displayOrder, {
            error: /** @type {MediaSlotError} */ ({
              feedbackType: 'error',
              helperText: error?.message || 'Failed to upload file. Please try again.',
            }),
          })
        )
        dispatch(
          setToastNotifications({
            show: true,
            message: error?.message || 'Failed to save media changes',
            type: 'error',
          })
        )
        throw error
      } finally {
        uploadEntries.forEach(entry => updateSlotUiState(entry.displayOrder, {uploading: false}))
      }
    },
    [dispatch, pendingDeletions, productId, updateSlotUiState, uploadFileOnBlob, uploadVideoOrGifOnBlob]
  )

  useEffect(() => {
    registerMediaSaveHandler(commitPendingMedia)
    return () => registerMediaSaveHandler(null)
  }, [registerMediaSaveHandler, commitPendingMedia])

  return {
    mediaSlots,
    handleUpload,
    handleUploadError,
    handleDelete,
    handleReorder,
  }
}
